/**
 * Caregiver API client
 *
 * Thin wrapper around fetch.  All app code imports from here rather than
 * calling fetch directly so we have one place to:
 *   - set base URL
 *   - attach auth headers (Phase 3+)
 *   - handle 401 / token refresh
 *   - normalise error shapes
 *
 * Usage:
 *   import api from "@/api/client";
 *   const client = await api.get("/clients/abc123");
 *   const result = await api.post("/clients", { display_name: "Maris" });
 */

const BASE_URL = "/api";

class ApiError extends Error {
  constructor(status, body) {
    super(`API ${status}`);
    this.status = status;
    this.body = body;
  }
}

async function request(method, path, body = undefined, options = {}) {
  const url = `${BASE_URL}${path}`;

  const headers = {
    "Content-Type": "application/json",
    // Phase 3+: attach bearer token from session store
    // ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal: options.signal,
  });

  if (!response.ok) {
    let errorBody;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = { message: response.statusText };
    }
    throw new ApiError(response.status, errorBody);
  }

  // 204 No Content
  if (response.status === 204) return null;

  return response.json();
}

const api = {
  get: (path, options) => request("GET", path, undefined, options),
  post: (path, body, options) => request("POST", path, body, options),
  put: (path, body, options) => request("PUT", path, body, options),
  patch: (path, body, options) => request("PATCH", path, body, options),
  delete: (path, options) => request("DELETE", path, undefined, options),
  ApiError,
};

export default api;
